// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// proeisincs, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngUtilities from "@eisinc/App/Components/Common/XngUtilities";
import XngZone from "@eisinc/App/Components/Common/XngZone";
import MXModal from "@eisinc/App/Components/MXModal/MXModal";
import ScrollToAnchor from "@eisinc/App/Components/ScrollToAnchor/ScrollToAnchor";
import XngScriptInjector from "@eisinc/App/Components/XngScriptInjector/XngScriptInjector";
import useElementDimensions from "@eisinc/App/Hooks/UseElementDimensions";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { getIsCheckoutPage } from "@eisinc/App/Store/Selectors/CheckoutSelector";
import { layoutContainer, setCSSVariable, xScreen } from "@eisinc/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { setCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const HEADER_BREAKPOINT = "md";

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);

    return {
        isSticky: state.xHeader.isSticky,
        stickyMenu: state.xHeader.stickyMenu,
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        location,
        customSettings: getSettingsCollection(state).customSettings,
        isCheckoutPage: getIsCheckoutPage(state),
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // XNG-Changes
    navigationContainer?: InjectableCss;
    innerNavigationContainer?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isSticky,
    stickyMenu,
    location,
    customSettings,
    isCheckoutPage,
}: Props) => {
    const [headerRef, size] = useElementDimensions<HTMLDivElement>();

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    const updateIframesSrc = () => {
        const utmCollection = getCookie("UtmCollection") ?? "";

        if (!utmCollection) {
            return;
        }

        const paramsFromCookie = new URLSearchParams(
            decodeURIComponent(utmCollection.replace(/%26/g, "&").replace(/%3D/g, "=")),
        );

        const iframes = document.querySelectorAll('iframe[id^="JotFormIFrame"]');

        if (!iframes.length) {
            return;
        }

        iframes.forEach((iframe: any) => {
            const iframeSrc = new URL(iframe.src);
            const iframeParams = new URLSearchParams(iframeSrc.search);

            paramsFromCookie.forEach((value, key) => {
                if (iframeParams.has(key)) {
                    iframeParams.set(key, value);
                } else {
                    iframeParams.append(key, value);
                }
            });

            iframeSrc.search = iframeParams.toString();
            iframe.src = iframeSrc.toString();
        });
    };

    const populateUtmCollectionCookie = () => {
        const utmCollectionSetting = (customSettings as any).utmCollection?.split(",");

        if (location && location.search.trim().length > 0 && location.search.indexOf(encodeURI("<script>")) === -1) {
            let utmParameters = location.search;
            if (utmParameters.startsWith("?")) {
                utmParameters = utmParameters.replace("?", "");
            }

            const filteredParams: Record<string, string> = {};

            const paramsArray = utmParameters.split("&");

            paramsArray.forEach(param => {
                const [key, value] = param.split("=");
                if (utmCollectionSetting?.includes(key)) {
                    filteredParams[key] = value;
                }
            });

            const filteredQueryString = Object.entries(filteredParams)
                .map(([key, value]) => `${key}=${value}`)
                .join("&");

            if (filteredQueryString) {
                setCookie("UtmCollection", filteredQueryString);
            }
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(updateIframesSrc, 400);
        return () => clearTimeout(timeoutId);
    }, [location.pathname]);

    // XNG-Changes
    useEffect(() => {
        setCSSVariable("--header-height", `${size?.height || 112}px`);
    }, [size?.height]);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }

        populateUtmCollectionCookie();
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            <StyledWrapper
                {...styles.page}
                as="header"
                isHeader={true}
                // xng-change
                role="banner"
                ref={headerRef}
            >
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {isCheckoutPage ? (
                        <XngZone contentId={id} zoneName="SecondaryNavigation" />
                    ) : (
                        <>
                            <XngZone contentId={id} zoneName="GlobalWebsiteMessage" />
                            <XngZone contentId={id} zoneName="SecondaryNavigation" />
                            <StyledWrapper {...styles.navigationContainer} {...({ isSticky, stickyMenu } as any)}>
                                <StyledWrapper {...styles.innerNavigationContainer}>
                                    <XngZone contentId={id} zoneName="MainNavigation" />
                                </StyledWrapper>
                            </StyledWrapper>
                        </>
                    )}
                    <XngZone contentId={id} zoneName="Scripts" />
                    <XngZone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
                <XngScriptInjector />
            </StyledWrapper>
            {/* XNG-Changes */}
            <XngUtilities />
            <MXModal />
            <ScrollToAnchor />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
